import { useCallback, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import type { LicenseType } from 'features/licenses/types'
import type { DomainType } from 'features/domains/types'

import { selectDomainDealers } from 'features/rootDomains/selectors'
import SelectedLicenseTable from '../SelectedLicenseTable'
import {
  TransferLicenseModalTitle,
  TransferLicenseModalParagraph,
} from './styles'
import FullscreenModal from 'components/Modal/FullscreenModal'
import TransferLicenseTable from './TransferLicenseTable'
import { prettyLicenseDescription } from 'features/licenses/utils'
import { transferLicenseThunk } from 'features/licenses/slice'
import { transferLicenseSelector } from 'features/licenses/selectors'

interface TransferLicenseModalProps {
  isOpen: boolean
  handleClose: VoidFunction
  license: LicenseType | null
}

/**
 * @todo localization
 */

function TransferLicenseModal({
  isOpen,
  handleClose,
  license,
}: TransferLicenseModalProps) {
  const dispatch = useAppDispatch()
  const knownNonDirectDealers = useAppSelector(selectDomainDealers.data) || []
  const isFetchingTransferLicense = useAppSelector(
    transferLicenseSelector.isFetching
  )

  const [targetDealer, setTargetDealer] = useState<DomainType | null>(null)

  const onClose = useCallback(() => {
    setTargetDealer(null)
    handleClose()
  }, [handleClose])

  const handleTransferLicense = useCallback(async () => {
    if (license && license?.purchaserDomainId && license?.id && targetDealer) {
      await dispatch(
        transferLicenseThunk(
          license.purchaserDomainId,
          license.id,
          targetDealer.id
        )
      )
      setTargetDealer(null)
      onClose()
    } else {
      console.error(
        `Some data is not defined: `,
        license?.purchaserDomainId,
        license?.id,
        targetDealer?.id
      )
    }
  }, [dispatch, onClose, license, targetDealer])

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={onClose}
      headerTitle="Transfer License"
      footerCloseText="Cancel"
      footerConfirmText="Transfer"
      onConfirm={handleTransferLicense}
      disabledConfirm={!targetDealer || !license || isFetchingTransferLicense}
    >
      <>
        <TransferLicenseModalTitle>Selected License</TransferLicenseModalTitle>
        <SelectedLicenseTable license={license ? [license] : []} />

        {targetDealer ? (
          <TransferLicenseModalParagraph>
            Transfer this{' '}
            {license && prettyLicenseDescription(license.description)} license
            to {targetDealer ? targetDealer.name : 'dealer'}. They will be able
            to view and apply this license using SCM.
          </TransferLicenseModalParagraph>
        ) : (
          <TransferLicenseModalParagraph>
            Select a dealer to transfer this license.
          </TransferLicenseModalParagraph>
        )}

        <TransferLicenseModalTitle>Select Dealer</TransferLicenseModalTitle>

        <TransferLicenseTable
          dealers={knownNonDirectDealers}
          targetDealer={targetDealer}
          onTargetDealerChange={setTargetDealer}
        />
      </>
    </FullscreenModal>
  )
}

export default TransferLicenseModal
